import {
  buildEntityCallbacks,
  type EntityCollection,
  type PropertyOrBuilder,
} from "@firecms/core";

import type { LocalizedString } from "./constants";
import { LocalizedStringField } from "./localizedStringField";
import { hasLocalizedStringValue } from "./resolveLocalizedString";

const DEFAULT_REQUIRED_MESSAGE = "At least one translation is required";

function isPropertyBuilder(
  property: PropertyOrBuilder | undefined
): property is (...args: never[]) => PropertyOrBuilder {
  return typeof property === "function";
}

function assertRequiredLocalizedStringsAt(
  properties: Record<string, PropertyOrBuilder> | undefined,
  values: unknown,
  pathPrefix = ""
): void {
  if (!properties || values == null || typeof values !== "object") {
    return;
  }

  const record = values as Record<string, unknown>;

  for (const [key, property] of Object.entries(properties)) {
    if (!property || isPropertyBuilder(property)) {
      continue;
    }

    const path = pathPrefix ? `${pathPrefix}.${key}` : key;
    const value = record[key];

    if (property.Field === LocalizedStringField) {
      if (
        property.validation?.required &&
        !hasLocalizedStringValue(value as LocalizedString)
      ) {
        const label = property.name ?? key;
        throw new Error(
          property.validation.requiredMessage ??
            `${label}: ${DEFAULT_REQUIRED_MESSAGE}`
        );
      }
      continue;
    }

    if (property.dataType === "map" && property.properties) {
      assertRequiredLocalizedStringsAt(property.properties, value, path);
      continue;
    }

    if (
      property.dataType === "array" &&
      property.of &&
      typeof property.of === "object" &&
      !isPropertyBuilder(property.of) &&
      Array.isArray(value)
    ) {
      const ofProperty = property.of;

      value.forEach((item, index) => {
        const itemPath = `${path}[${index}]`;

        if (ofProperty.dataType === "map" && ofProperty.properties) {
          assertRequiredLocalizedStringsAt(ofProperty.properties, item, itemPath);
        }
      });
    }
  }
}

export function assertRequiredLocalizedStrings(
  properties: Record<string, PropertyOrBuilder> | undefined,
  values: unknown
): void {
  assertRequiredLocalizedStringsAt(properties, values);
}

export function withLocalizedStringValidation<T extends Record<string, unknown>>(
  collection: EntityCollection<T>
): EntityCollection<T> {
  const previousOnPreSave = collection.callbacks?.onPreSave;

  return {
    ...collection,
    callbacks: buildEntityCallbacks({
      ...collection.callbacks,
      onPreSave: async (props) => {
        assertRequiredLocalizedStrings(props.collection.properties, props.values);

        if (previousOnPreSave) {
          return previousOnPreSave(props);
        }

        return props.values;
      },
    }),
  };
}
